/**
 * Charity-specific enhancement blocks for FoodBusinessPage.
 *
 *   <CharityImpactBadges biz={biz} />   — 4 large stat cards
 *   <CharityContactForm  biz={biz} />   — donor/volunteer contact form
 *
 * Both render only when the business is a food charity (food_bank,
 * food_charity, food_pantry, soup_kitchen). The parent page passes
 * the whole `biz` object, so this component decides visibility itself.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Heart, Users, Utensils, Recycle, Loader2, CheckCircle2, Send, Info,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const CHARITY_TYPES = new Set(["food_bank", "food_charity", "food_pantry", "soup_kitchen"]);

// ─── Impact badges ──────────────────────────────────────────────────
const BADGE_STYLES = {
  meals:     { icon: Utensils, label: "Meals served",   color: "#B25D38", bg: "#FFE9D0" },
  families:  { icon: Users,    label: "Families helped", color: "#0F4C81", bg: "#DDECFA" },
  rescued:   { icon: Recycle,  label: "Food rescued",   color: "#166534", bg: "#DCEAD1" },
  volunteers:{ icon: Heart,    label: "Volunteer hours", color: "#8B5E10", bg: "#F0E2C7" },
};

/**
 * `biz.impact` is an optional object stored on the food_businesses doc
 * that owners can edit from the business dashboard. Shape:
 *
 *   impact: {
 *     meals_served: number,
 *     families_helped: number,
 *     food_rescued_kg: number,
 *     volunteer_hours: number,
 *     last_updated: ISO date string
 *   }
 *
 * When no impact data has been recorded yet we still render the block
 * with an inviting placeholder that prompts the owner to claim & report.
 */
export function CharityImpactBadges({ biz }) {
  if (!biz || !CHARITY_TYPES.has(biz.business_type)) return null;
  const impact = biz.impact || {};
  const rows = [
    ["meals",      impact.meals_served,     formatCount],
    ["families",   impact.families_helped,  formatCount],
    ["rescued",    impact.food_rescued_kg,  (v) => `${formatCount(v)} kg`],
    ["volunteers", impact.volunteer_hours,  (v) => `${formatCount(v)} hrs`],
  ];
  const anyValue = rows.some(([, v]) => v && v > 0);

  return (
    <div className="rounded-2xl border bg-white p-5"
         style={{ borderColor: "#E9D8B4" }}
         data-testid="charity-impact-badges">
      <div className="flex items-center justify-between mb-3">
        <div className="font-display text-lg" style={{ color: "#3E2A0A" }}>
          Community impact
        </div>
        {impact.last_updated && (
          <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">
            Updated {new Date(impact.last_updated).toLocaleDateString()}
          </div>
        )}
      </div>

      {anyValue ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {rows.map(([kind, value, fmt]) => {
            const cfg = BADGE_STYLES[kind];
            const Icon = cfg.icon;
            return (
              <div key={kind}
                   className="rounded-xl p-4 text-center"
                   style={{ background: cfg.bg }}
                   data-testid={`charity-badge-${kind}`}>
                <Icon className="w-6 h-6 mx-auto mb-2" style={{ color: cfg.color }} />
                <div className="font-display text-2xl font-bold" style={{ color: cfg.color }}>
                  {value && value > 0 ? fmt(value) : "—"}
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest mt-1"
                     style={{ color: cfg.color, opacity: 0.85 }}>
                  {cfg.label}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-sm opacity-80 rounded-lg p-4 border border-dashed flex gap-3"
             style={{ borderColor: "#C79A3F", background: "#FFF9EA" }}>
          <Info className="w-5 h-5 flex-none mt-0.5" style={{ color: "#8B5E10" }} />
          <div>
            <div className="font-semibold mb-1">Impact reporting is coming soon.</div>
            <div className="text-xs opacity-80">
              If you run this charity, claim the listing to publish live counters
              — meals served, families helped, food rescued, and volunteer hours.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function formatCount(n) {
  if (!n) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(n < 10_000 ? 1 : 0)}k`;
  return Number(n).toLocaleString();
}


// ─── Contact form ────────────────────────────────────────────────────
const PURPOSES = [
  { value: "donor",       label: "I want to donate food or funds" },
  { value: "volunteer",   label: "I want to volunteer" },
  { value: "partner",     label: "Partnership / business" },
  { value: "beneficiary", label: "I'm looking for food assistance" },
  { value: "other",       label: "Something else" },
];

export function CharityContactForm({ biz }) {
  const isCharity = biz && CHARITY_TYPES.has(biz.business_type);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", purpose: "donor", message: "",
  });
  const [busy, setBusy]   = useState(false);
  const [done, setDone]   = useState(null);
  const [count, setCount] = useState(null);

  useEffect(() => {
    if (!isCharity) return;
    axios.get(`${API}/api/charities/${biz.slug}/contact-count`)
      .then((r) => setCount(r.data.count))
      .catch(() => setCount(null));
  }, [isCharity, biz?.slug]);

  const canSubmit = useMemo(() =>
    form.name.trim().length >= 2 &&
    /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim()) &&
    form.message.trim().length >= 5,
  [form]);

  if (!isCharity) return null;

  async function submit(e) {
    e?.preventDefault();
    if (!canSubmit || busy) return;
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/api/charities/${biz.slug}/contact`,
        { ...form, name: form.name.trim(), email: form.email.trim(), message: form.message.trim() },
      );
      setDone(res.data);
      setCount((c) => (typeof c === "number" ? c + 1 : c));
      toast.success("Message sent.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't send your message. Try again.");
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="rounded-2xl border bg-white p-6" style={{ borderColor: "#E9D8B4" }}
           data-testid="charity-contact-done">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-6 h-6 flex-none" style={{ color: "#166534" }} />
          <div>
            <div className="font-display text-lg" style={{ color: "#3E2A0A" }}>
              Message sent.
            </div>
            <p className="text-sm opacity-80 mt-1">{done.message}</p>
            <button type="button" onClick={() => { setDone(null); setForm({ name: "", email: "", phone: "", purpose: "donor", message: "" }); }}
                    className="mt-4 text-xs underline opacity-70 hover:opacity-100"
                    data-testid="charity-contact-send-another">
              Send another message
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit}
          className="rounded-2xl border bg-white p-5 space-y-3"
          style={{ borderColor: "#E9D8B4" }}
          data-testid="charity-contact-form">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-display text-lg" style={{ color: "#3E2A0A" }}>
            Contact this charity
          </div>
          <div className="text-xs opacity-70 mt-0.5">
            Reach {biz.name} directly to donate, volunteer, or ask about assistance.
          </div>
        </div>
        {typeof count === "number" && count > 0 && (
          <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full"
                style={{ background: "#F0E2C7", color: "#8B5E10" }}
                data-testid="charity-contact-count">
            {count} contacted
          </span>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Your name" testid="charity-contact-name">
          <input type="text" required minLength={2} value={form.name}
                 onChange={(e) => setForm({ ...form, name: e.target.value })}
                 className="w-full rounded-lg border px-3 py-2 text-sm bg-white"
                 style={{ borderColor: "#E9D8B4" }}
                 placeholder="Priya Iyer"
                 data-testid="charity-contact-name-input" />
        </Field>
        <Field label="Email" testid="charity-contact-email">
          <input type="email" required value={form.email}
                 onChange={(e) => setForm({ ...form, email: e.target.value })}
                 className="w-full rounded-lg border px-3 py-2 text-sm bg-white"
                 style={{ borderColor: "#E9D8B4" }}
                 placeholder="priya@example.com"
                 data-testid="charity-contact-email-input" />
        </Field>
        <Field label="Phone (optional)" testid="charity-contact-phone">
          <input type="tel" value={form.phone}
                 onChange={(e) => setForm({ ...form, phone: e.target.value })}
                 className="w-full rounded-lg border px-3 py-2 text-sm bg-white"
                 style={{ borderColor: "#E9D8B4" }}
                 placeholder="+1 555 123 4567"
                 data-testid="charity-contact-phone-input" />
        </Field>
        <Field label="I'm reaching out because…" testid="charity-contact-purpose">
          <select value={form.purpose}
                  onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                  className="w-full rounded-lg border px-3 py-2 text-sm bg-white"
                  style={{ borderColor: "#E9D8B4" }}
                  data-testid="charity-contact-purpose-input">
            {PURPOSES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </Field>
      </div>

      <Field label="Message" testid="charity-contact-message">
        <textarea required minLength={5} rows={4} value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full rounded-lg border px-3 py-2 text-sm bg-white"
                  style={{ borderColor: "#E9D8B4" }}
                  placeholder="Tell them what you'd like to donate, when you can volunteer, or what kind of assistance you need."
                  data-testid="charity-contact-message-input" />
      </Field>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-[10px] opacity-60 max-w-md">
          FoodPulse will pass your message to the charity. Your email is never sold or shared.
        </div>
        <button type="submit" disabled={!canSubmit || busy}
                className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-white font-semibold disabled:opacity-50 hover:shadow-md transition-shadow"
                style={{ background: "#B25D38" }}
                data-testid="charity-contact-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {busy ? "Sending…" : "Send message"}
        </button>
      </div>
    </form>
  );
}


function Field({ label, testid, children }) {
  return (
    <label className="block" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-widest opacity-70 mb-1">
        {label}
      </div>
      {children}
    </label>
  );
}
